import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { requestService } from '../services/requestService'
import { employeeService } from '../services/employeeService'
import { LicenseRequest, REQUEST_TYPE_LABELS, RequestType } from '../types'
import {
  Users,
  FileText,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  Plus,
  ArrowRight,
  Calendar,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'

interface Stats {
  employees: {
    total: number
    active: number
  }
  requests: {
    total: number
    pending: number
    approved: number
    denied: number
  }
}

interface CalendarDay {
  date: Date
  isCurrentMonth: boolean
  isToday: boolean
  requests: LicenseRequest[]
}

export function DashboardPage() {
  const { user } = useAuth()
  const isAdmin = user?.role === 'ADMIN'
  const [stats, setStats] = useState<Stats | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [approvedRequests, setApprovedRequests] = useState<LicenseRequest[]>([])
  const [currentMonth, setCurrentMonth] = useState(new Date())

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const employeeId = !isAdmin && user?.employeeId ? user.employeeId : undefined

        const [empStats, reqStats] = await Promise.all([
          isAdmin ? employeeService.getStats() : Promise.resolve({ total: 0, active: 0 }),
          requestService.getStats({ employeeId }),
        ])

        setStats({
          employees: {
            total: empStats.total,
            active: empStats.active,
          },
          requests: {
            total: reqStats.total,
            pending: reqStats.byStatus.PENDING || 0,
            approved: reqStats.byStatus.APPROVED || 0,
            denied: reqStats.byStatus.DENIED || 0,
          },
        })

        // Obtener solicitudes aprobadas para el calendario (solo empleados)
        if (!isAdmin && user?.employeeId) {
          const approvedRes = await requestService.getAll({
            employeeId: user.employeeId,
            status: 'APPROVED',
            limit: 50,
          })
          setApprovedRequests(approvedRes.data)
        }
      } catch (error) {
        console.error('Error fetching stats:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchStats()
  }, [isAdmin, user?.employeeId])

  // Funciones del calendario
  const getCalendarDays = (): CalendarDay[] => {
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth()
    const firstDayOfMonth = new Date(year, month, 1)
    const lastDayOfMonth = new Date(year, month + 1, 0)
    const startDay = firstDayOfMonth.getDay() === 0 ? 6 : firstDayOfMonth.getDay() - 1 // Lunes = 0

    const days: CalendarDay[] = []
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    // Días del mes anterior
    for (let i = startDay - 1; i >= 0; i--) {
      const date = new Date(year, month, -i)
      days.push({
        date,
        isCurrentMonth: false,
        isToday: false,
        requests: getRequestsForDate(date),
      })
    }

    // Días del mes actual
    for (let day = 1; day <= lastDayOfMonth.getDate(); day++) {
      const date = new Date(year, month, day)
      days.push({
        date,
        isCurrentMonth: true,
        isToday: date.getTime() === today.getTime(),
        requests: getRequestsForDate(date),
      })
    }

    // Días del mes siguiente para completar la semana
    const remainingDays = 42 - days.length
    for (let i = 1; i <= remainingDays; i++) {
      const date = new Date(year, month + 1, i)
      days.push({
        date,
        isCurrentMonth: false,
        isToday: false,
        requests: getRequestsForDate(date),
      })
    }

    return days
  }

  const getRequestsForDate = (date: Date): LicenseRequest[] => {
    return approvedRequests.filter((req) => {
      const start = new Date(req.startDate)
      const end = new Date(req.endDate)
      start.setHours(0, 0, 0, 0)
      end.setHours(0, 0, 0, 0)
      date.setHours(0, 0, 0, 0)
      // Check normal date range
      if (date >= start && date <= end) return true
      // Also check secondDate for DLE with non-consecutive days
      if (req.secondDate) {
        const second = new Date(req.secondDate)
        second.setHours(0, 0, 0, 0)
        if (date.getTime() === second.getTime()) return true
      }
      return false
    })
  }

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))
  }

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))
  }

  const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
  const dayNames = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']

  const statCards = isAdmin
    ? [
        {
          name: 'Empleados Activos',
          value: stats?.employees.active || 0,
          icon: Users,
          gradient: 'from-primary-500 to-primary-700',
          href: '/employees',
        },
        {
          name: 'Pendientes',
          value: stats?.requests.pending || 0,
          icon: Clock,
          gradient: 'from-secondary-500 to-secondary-700',
          href: '/requests?status=PENDING',
        },
        {
          name: 'Aprobadas',
          value: stats?.requests.approved || 0,
          icon: CheckCircle,
          gradient: 'from-success-500 to-success-600',
          href: '/requests?status=APPROVED',
        },
        {
          name: 'Denegadas',
          value: stats?.requests.denied || 0,
          icon: XCircle,
          gradient: 'from-error-500 to-error-600',
          href: '/requests?status=DENIED',
        },
      ]
    : [
        {
          name: 'Mis Solicitudes',
          value: stats?.requests.total || 0,
          icon: FileText,
          gradient: 'from-primary-500 to-primary-700',
          href: '/requests',
        },
        {
          name: 'Pendientes',
          value: stats?.requests.pending || 0,
          icon: Clock,
          gradient: 'from-secondary-500 to-secondary-700',
          href: '/requests?status=PENDING',
        },
        {
          name: 'Aprobadas',
          value: stats?.requests.approved || 0,
          icon: CheckCircle,
          gradient: 'from-success-500 to-success-600',
          href: '/requests?status=APPROVED',
        },
        {
          name: 'Denegadas',
          value: stats?.requests.denied || 0,
          icon: XCircle,
          gradient: 'from-error-500 to-error-600',
          href: '/requests?status=DENIED',
        },
      ]

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-4 sm:space-y-6 lg:space-y-8 animate-slide-in">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title text-xl sm:text-2xl lg:text-3xl">Pantalla principal</h1>
        <p className="page-subtitle text-sm sm:text-base">
          Bienvenido, {user?.name}
          <span className="hidden sm:inline">. Aquí tienes un resumen del sistema.</span>
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
        {statCards.map((stat) => (
          <Link
            key={stat.name}
            to={stat.href}
            className="stat-card group p-3 sm:p-4 lg:p-6"
          >
            <div className={`stat-icon bg-gradient-to-br ${stat.gradient} h-10 w-10 sm:h-12 sm:w-12`}>
              <stat.icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                {stat.name}
              </p>
              <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                {stat.value}
              </p>
            </div>
            <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 text-gray-300 dark:text-gray-600 group-hover:text-primary-500 group-hover:translate-x-1 transition-all hidden xs:block" />
          </Link>
        ))}
      </div>

      {/* Quick Actions & Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <div className="card p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6 flex items-center gap-2">
            <div className="p-1.5 sm:p-2 rounded-lg bg-gradient-to-br from-primary-500 to-primary-700">
              <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
            </div>
            Acciones Rápidas
          </h2>
          <div className="space-y-2 sm:space-y-3">
            <Link
              to="/requests/new"
              className="flex items-center justify-center gap-2 w-full btn btn-primary py-2.5 sm:py-3 text-sm sm:text-base"
            >
              <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
              Nueva Solicitud
            </Link>
            {isAdmin && (
              <Link
                to="/employees"
                className="flex items-center justify-center gap-2 w-full btn btn-secondary py-2.5 sm:py-3 text-sm sm:text-base"
              >
                <Users className="h-4 w-4 sm:h-5 sm:w-5" />
                Ver Empleados
              </Link>
            )}
            <Link
              to="/requests"
              className="flex items-center justify-center gap-2 w-full btn btn-secondary py-2.5 sm:py-3 text-sm sm:text-base"
            >
              <FileText className="h-4 w-4 sm:h-5 sm:w-5" />
              {isAdmin ? 'Ver Solicitudes' : 'Mis Solicitudes'}
            </Link>
          </div>
        </div>

        <div className="card p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-4 sm:mb-6 flex items-center gap-2">
            <div className="p-1.5 sm:p-2 rounded-lg bg-gradient-to-br from-secondary-500 to-secondary-700">
              <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
            </div>
            {isAdmin ? 'Resumen General' : 'Mis Estadísticas'}
          </h2>
          <div className="space-y-1">
            <div className="flex justify-between items-center py-2 sm:py-3 border-b border-gray-100 dark:border-dark-border">
              <span className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                {isAdmin ? 'Total Solicitudes' : 'Mis Solicitudes'}
              </span>
              <span className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                {stats?.requests.total || 0}
              </span>
            </div>
            {isAdmin && (
              <div className="flex justify-between items-center py-2 sm:py-3 border-b border-gray-100 dark:border-dark-border">
                <span className="text-sm sm:text-base text-gray-600 dark:text-gray-400">Total Empleados</span>
                <span className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                  {stats?.employees.total || 0}
                </span>
              </div>
            )}
            <div className="flex justify-between items-center py-2 sm:py-3 border-b border-gray-100 dark:border-dark-border">
              <span className="text-sm sm:text-base text-gray-600 dark:text-gray-400">Pendientes</span>
              <span className="text-base sm:text-lg font-semibold text-secondary-600 dark:text-secondary-400">
                {stats?.requests.pending || 0}
              </span>
            </div>
            <div className="flex justify-between items-center py-2 sm:py-3">
              <span className="text-sm sm:text-base text-gray-600 dark:text-gray-400">Tasa de Aprobación</span>
              <span className="text-base sm:text-lg font-semibold text-success-600 dark:text-success-500">
                {stats?.requests.total
                  ? Math.round(
                      (stats.requests.approved / stats.requests.total) * 100
                    )
                  : 0}
                %
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Calendario de Permisos - Solo para empleados */}
      {!isAdmin && (
        <div className="card p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <div className="p-1.5 sm:p-2 rounded-lg bg-gradient-to-br from-primary-500 to-primary-700">
                <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
              </div>
              <span className="hidden xs:inline">Mis Permisos Aprobados</span>
              <span className="xs:hidden">Permisos</span>
            </h2>
            <div className="flex items-center justify-center gap-1 sm:gap-2">
              <button
                onClick={prevMonth}
                className="p-1.5 sm:p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600 dark:text-gray-400" />
              </button>
              <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white min-w-[100px] sm:min-w-[140px] text-center">
                {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </span>
              <button
                onClick={nextMonth}
                className="p-1.5 sm:p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>
          </div>

          {/* Días de la semana */}
          <div className="grid grid-cols-7 gap-0.5 sm:gap-1 mb-1 sm:mb-2">
            {dayNames.map((day) => (
              <div
                key={day}
                className="text-center text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-400 py-1 sm:py-2"
              >
                {day.substring(0, 1)}<span className="hidden xs:inline">{day.substring(1)}</span>
              </div>
            ))}
          </div>

          {/* Días del calendario */}
          <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
            {getCalendarDays().map((day, index) => (
              <div
                key={index}
                className={`
                  relative min-h-[40px] sm:min-h-[60px] p-0.5 sm:p-1 rounded-md sm:rounded-lg border transition-colors
                  ${day.isCurrentMonth
                    ? 'bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700'
                    : 'bg-gray-50 dark:bg-slate-900 border-gray-100 dark:border-slate-800'
                  }
                  ${day.isToday ? 'ring-1 sm:ring-2 ring-primary-500' : ''}
                `}
              >
                <span
                  className={`
                    text-[10px] sm:text-xs font-medium
                    ${day.isCurrentMonth
                      ? 'text-gray-900 dark:text-white'
                      : 'text-gray-400 dark:text-gray-600'
                    }
                    ${day.isToday ? 'text-primary-600 dark:text-primary-400' : ''}
                  `}
                >
                  {day.date.getDate()}
                </span>
                {day.requests.length > 0 && (
                  <div className="mt-0.5 sm:mt-1 space-y-0.5">
                    {day.requests.slice(0, 1).map((req) => (
                      <Link
                        key={req.id}
                        to={`/requests/${req.id}`}
                        className="block text-[8px] sm:text-[10px] truncate px-0.5 sm:px-1 py-0.5 rounded bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-400 hover:bg-success-200 dark:hover:bg-success-900/50 transition-colors"
                        title={REQUEST_TYPE_LABELS[req.requestType as RequestType]}
                      >
                        <span className="hidden sm:inline">{REQUEST_TYPE_LABELS[req.requestType as RequestType].substring(0, 12)}...</span>
                        <span className="sm:hidden">OK</span>
                      </Link>
                    ))}
                    {day.requests.length > 1 && (
                      <span className="block text-[8px] sm:text-[10px] text-gray-500 dark:text-gray-400 px-0.5 sm:px-1">
                        +{day.requests.length - 1}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Leyenda */}
          <div className="mt-3 sm:mt-4 flex flex-wrap items-center gap-3 sm:gap-4 text-[10px] sm:text-xs text-gray-600 dark:text-gray-400">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded bg-success-100 dark:bg-success-900/30 border border-success-300 dark:border-success-700"></div>
              <span>Permiso aprobado</span>
            </div>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded ring-1 sm:ring-2 ring-primary-500"></div>
              <span>Hoy</span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
